import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

/**
 * Custom hook for real-time data updates
 * @param {Function} fetchFunction - Function to fetch data
 * @param {number} interval - Refresh interval in milliseconds (default: 30000)
 * @param {boolean} enableNotifications - Whether to show notifications on changes (default: true)
 * @param {Array} dependencies - Dependencies for the fetch function
 * @returns {Object} - { data, loading, error, lastRefresh, refresh }
 */
const useRealTimeData = (
  fetchFunction, 
  interval = 30000, 
  enableNotifications = true,
  dependencies = []
) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const refresh = useCallback(async (showNotification = false) => {
    setLoading(true);
    try {
      const result = await fetchFunction();
      
      // Use functional update to avoid dependency on data
      setData(prevData => {
        // Check if there are changes (compare with previous data)
        const hasChanges = JSON.stringify(result) !== JSON.stringify(prevData);
        
        // Show notification if there are changes and notification is requested
        if (showNotification && enableNotifications && hasChanges && prevData !== null) {
          toast.success('Data updated with new information!');
        }
        
        return result;
      });
      
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, enableNotifications]);

  // Initial load
  useEffect(() => {
    refresh();
  }, dependencies);

  // Auto-refresh on interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      refresh(true); // Show notification if changes detected
    }, interval);

    return () => clearInterval(intervalId);
  }, [refresh, interval]);

  // Refresh when user returns to the tab
  useEffect(() => {
    const handleFocus = () => {
      refresh(true); // Show notification if changes detected
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refresh]);

  return {
    data,
    loading,
    error,
    lastRefresh,
    refresh: () => refresh(false) // Manual refresh without notification
  };
};

export default useRealTimeData;




